import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Filter,
  AlertTriangle,
  Sparkles,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const FINDING_TYPES = ["insight", "trend", "contradiction", "opportunity", "risk"];

function confidenceBand(s) {
  if (s >= 0.75) return "high";
  if (s >= 0.5) return "medium";
  return "low";
}

function ConfidenceBadge({ score, lowExplicit = false }) {
  const { t } = useTranslation("findings");
  const band = confidenceBand(score);
  const cls =
    band === "high"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : band === "medium"
        ? "border-sky-200 bg-sky-50 text-sky-700"
        : "border-amber-200 bg-amber-50 text-amber-800";
  return (
    <Badge variant="outline" className={cls} data-testid={`finding-conf-${band}`}>
      {lowExplicit && score < 0.6 ? t("confidence.lowBadge") : t(`confidence.${band}`)} · {Math.round((score || 0) * 100)}%
    </Badge>
  );
}

export default function Findings() {
  const { t } = useTranslation(["findings", "common", "errors"]);

  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [type, setType] = useState("");
  const [minConfidence, setMinConfidence] = useState(0.6);
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  const [includeOrphaned, setIncludeOrphaned] = useState(false);
  const [hasContradictions, setHasContradictions] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/brands"), api.get("/research/projects")])
      .then(([b, p]) => { setBrands(b.data); setProjects(p.data); })
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (projectId) p.set("project_id", projectId);
    else if (brandId) p.set("brand_id", brandId);
    if (type) p.set("type", type);
    p.set("min_confidence", String(minConfidence));
    if (includeLowConfidence) p.set("include_low_confidence", "true");
    if (includeOrphaned) p.set("include_orphaned", "true");
    if (hasContradictions) p.set("has_contradictions", "true");
    p.set("page", String(page));
    p.set("page_size", String(pageSize));
    return p.toString();
  }, [brandId, projectId, type, minConfidence, includeLowConfidence, includeOrphaned, hasContradictions, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/findings?${params}`);
      setRows(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => { load(); }, [load]);

  const projectsByBrand = useMemo(() => brandId ? projects.filter((p) => p.brand_id === brandId) : projects, [projects, brandId]);
  const projectName = useCallback((pid) => projects.find((x) => x.id === pid)?.name || "—", [projects]);

  return (
    <div className="grid gap-8 lg:grid-cols-[280px,1fr]" data-testid="findings-page">
      <aside className="space-y-4" data-testid="findings-filters">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Filter className="h-3.5 w-3.5" strokeWidth={2} />
            {t("findings:library.filters.title")}
          </div>
          <button
            type="button"
            className="text-xs text-indigo-700 hover:underline"
            onClick={() => {
              setBrandId(""); setProjectId(""); setType(""); setMinConfidence(0.6);
              setIncludeLowConfidence(false); setHasContradictions(false); setPage(1);
            }}
            data-testid="findings-clear-filters"
          >
            {t("findings:library.filters.clear")}
          </button>
        </div>
        <FilterSelect label={t("findings:library.filters.brand")} value={brandId} onChange={(v) => { setBrandId(v); setProjectId(""); setPage(1); }} placeholder={t("findings:library.filters.anyBrand")} options={brands.map((b) => ({ value: b.id, label: b.name }))} testid="findings-filter-brand" />
        <FilterSelect label={t("findings:library.filters.project")} value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} placeholder={t("findings:library.filters.anyProject")} options={projectsByBrand.map((p) => ({ value: p.id, label: p.name }))} testid="findings-filter-project" />
        <FilterSelect label={t("findings:library.filters.type")} value={type} onChange={(v) => { setType(v); setPage(1); }} placeholder={t("findings:library.filters.anyType")} options={FINDING_TYPES.map((tp) => ({ value: tp, label: t(`type.${tp}`) }))} testid="findings-filter-type" />
        <div className="space-y-2">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">
            {t("findings:library.filters.minConfidence")} · {Math.round(minConfidence*100)}%
          </Label>
          <input type="range" min={0} max={1} step={0.05} value={minConfidence} onChange={(e) => { setMinConfidence(parseFloat(e.target.value)); setPage(1); }} className="w-full" data-testid="findings-filter-min-confidence" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-slate-600">{t("findings:library.filters.hasContradictions")}</Label>
          <Switch checked={hasContradictions} onCheckedChange={(v) => { setHasContradictions(v); setPage(1); }} data-testid="findings-filter-contradictions" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-slate-600">{t("findings:library.filters.includeLowConfidence")}</Label>
          <Switch checked={includeLowConfidence} onCheckedChange={(v) => { setIncludeLowConfidence(v); setPage(1); }} data-testid="findings-toggle-low-confidence" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs text-slate-600" title={t("findings:card.evidenceUnavailableTooltip")}>
            {includeOrphaned
              ? t("findings:filters.hideOrphaned")
              : t("findings:filters.showOrphaned", { count: 0 })}
          </Label>
          <Switch checked={includeOrphaned} onCheckedChange={(v) => { setIncludeOrphaned(v); setPage(1); }} data-testid="findings-toggle-orphaned" />
        </div>
      </aside>

      <main className="space-y-6">
        <header className="space-y-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <Lightbulb className="mr-1 inline h-3 w-3" strokeWidth={2} />
            EvidenceLab
          </span>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("findings:library.title")}</h1>
          <p className="max-w-xl text-sm text-slate-500">{t("findings:library.subtitle")}</p>
        </header>
        <p className="text-xs text-slate-500" data-testid="findings-count">
          {t("findings:library.showingOf", { shown: rows.length, total })}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-8 py-14 text-center text-sm text-slate-500" data-testid="findings-empty">
            {total === 0 ? t("findings:library.empty.body") : t("findings:library.noResults")}
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((f) => (
              <Link
                key={f.id}
                to={`/findings/${f.id}`}
                data-testid={`finding-card-${f.id}`}
                className="block rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-slate-300 hover:shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
                      <Sparkles className="mr-1 h-3 w-3" strokeWidth={2} />
                      {t(`type.${f.type}`)}
                    </Badge>
                    <span className="text-xs text-slate-500">{f.theme_label}</span>
                    {(f.contradictions || []).length > 0 && (
                      <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">
                        <AlertTriangle className="mr-1 h-3 w-3" strokeWidth={2} />
                        {t("card.contradictsOthers")}
                      </Badge>
                    )}
                  </div>
                  <ConfidenceBadge score={f.confidence} lowExplicit />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-900">{f.text}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span>{projectName(f.project_id)}</span>
                  <span>{f.evidence_count} pcs</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {total > pageSize && (
          <div className="flex items-center justify-between text-sm">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="findings-prev">Prev</Button>
            <span className="text-xs text-slate-500">Page {page} / {Math.ceil(total / pageSize)}</span>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} data-testid="findings-next">Next</Button>
          </div>
        )}
      </main>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={value || "__any__"} onValueChange={(v) => onChange(v === "__any__" ? "" : v)}>
        <SelectTrigger data-testid={testid} className="h-9 text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">{placeholder}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
